import { Api } from '@/api';
import type { WorkflowStatus } from '@/api/entity_types/workflow/Workflow';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { toast } from 'vue-sonner';
import { useWorkflowDefinitionsStore } from './workflowDefinitions';
import type { WorkflowActionEntity } from '@/api/entity_types/workflow/WorkflowAction';
import type { GetWorkflowResponse } from '@/api/workflows';

const emptyWorkflow: GetWorkflowResponse = {
  id: -1,
  name: '',
  description: '',
  status: 'inactive',
  team_id: 0,
  trigger_definition_key: 'candidate_created',
  created_at: '',
  updated_at: '',
  workflow_actions: [],
};

export const useCurrentWorkflowStore = defineStore('currentWorkflow', () => {
  const workflowDefinitionsStore = useWorkflowDefinitionsStore();
  const currentWorkflow = ref<GetWorkflowResponse>(emptyWorkflow);
  const isLoaded = ref<boolean>(false);

  const loadWorkflow = async (workflowId: number) => {
    currentWorkflow.value = emptyWorkflow;
    isLoaded.value = false;
    const response = await Api.Workflows.getWorkflow(workflowId);
    isLoaded.value = true;

    if (response.status === 'success') {
      currentWorkflow.value = response.data;
    } else {
      const description = response.messages.join(', ') || 'Network Error';
      toast.error('Current Workflow Error', { description });
    }
  };

  const currentDefinition = computed(() => {
    return workflowDefinitionsStore.workflowDefinitions.find(
      definition => definition.trigger.key === currentWorkflow.value.trigger_definition_key
    )!;
  });

  const pushAction = (action: WorkflowActionEntity) => {
    currentWorkflow.value.workflow_actions.push(action);
  };

  const updateAction = (action: WorkflowActionEntity) => {
    const index = currentWorkflow.value.workflow_actions.findIndex(x => x.id === action.id);
    currentWorkflow.value.workflow_actions[index] = action;
    removeExitCriteriaUnlessScheduledActions();
  };

  const removeAction = (action: WorkflowActionEntity) => {
    const index = currentWorkflow.value.workflow_actions.findIndex(x => x.id === action.id);
    currentWorkflow.value.workflow_actions.splice(index, 1);
    removeExitCriteriaUnlessScheduledActions();
  };

  const updateStatus = (newStatus: WorkflowStatus) => {
    currentWorkflow.value.status = newStatus;
  };

  const update = ({ name, description }: { name: string; description: string }) => {
    currentWorkflow.value.name = name;
    currentWorkflow.value.description = description;
  };

  const removeExitCriteriaUnlessScheduledActions = () => {
    const scheduledActions = currentWorkflow.value.workflow_actions.filter(x => x.invocation === 'scheduled');
    if (scheduledActions.length === 0) {
      currentWorkflow.value.exit_criteria_condition = undefined;
    }
  };

  return {
    currentWorkflow,
    isLoaded,
    currentDefinition,
    loadWorkflow,
    pushAction,
    updateAction,
    removeAction,
    updateStatus,
    update,
  };
});
